import type { JSX } from 'react';
import { Col, FormGroup, FormSection, Radio, Row, Text } from '@ksyos/design-system';
import type { BariPreNurseSpecialistMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/types';
import {
    ConsistentieOntlastingOpties,
    OntlastingspatroonOpties,
    PatientIsVerwezenDoorOpties,
} from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/bariPreNurseSpecialistMedScreeningReportData';
import {
    NumberInputContext,
    RadioGroupFormGroup,
    SelectFormGroup,
    TextAreaFormGroup,
    TextInputFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { YesNoWithConditionalTextAreaFormGroup } from '@frontend/components/pages/episodeCategory/customControls/YesNoWithConditionalTextAreaFormGroup';
import { useProcessActivityEditContext } from '@frontend/utils';
import { sortedConsistentieOntlastingOpties } from './bariPreNurseSpecialistMedScreeningReportUtils';

export function BariPreNurseSpecialistMedScreeningReportSpecieleAnamnese(): JSX.Element {
    const { data, errors, onChange } = useProcessActivityEditContext<BariPreNurseSpecialistMedScreeningReportData>();

    return (
        <FormSection title="Speciële anamnese">
            <RadioGroupFormGroup
                fieldKey="patient_is_verwezen_door"
                isRequired={true}
                label="Patiënt is verwezen door"
            >
                {Object.values(PatientIsVerwezenDoorOpties).map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <FormGroup
                hasErrors={errors.has('duur_overgewicht_in_jaren')}
                isRequired={true}
                label="Duur overgewicht"
            >
                <Row columnGap={3}>
                    <Col span={1}>
                        <NumberInputContext
                            fieldKey="duur_overgewicht_in_jaren"
                            label="Duur overgewicht"
                            maximumFractionDigits={1}
                        />
                    </Col>
                    <Col>
                        <Text
                            fontWeight="medium"
                            mt={2}
                            size="sm"
                        >
                            jaar
                        </Text>
                    </Col>
                </Row>
            </FormGroup>
            <TextAreaFormGroup
                fieldKey="motivatie_behandeling"
                isRequired={true}
                label="Motivatie behandeling"
            />
            <YesNoWithConditionalTextAreaFormGroup
                fieldKey="buikklachten"
                fieldValue={data.buikklachten}
                isRequired={true}
                label="Sprake van buikklachten"
                textAreaFieldKey="buikklachten_toelichting"
                textAreaLabel="Toelichting buikklachten"
                toolTipText="(IBS, IBD, etc.)"
            />
            <RadioGroupFormGroup
                fieldKey="ontlastingspatroon"
                isRequired={true}
                label="Ontlastingspatroon"
                onChange={handleChangeOntlastingspatroon}
            >
                {[OntlastingspatroonOpties.dagelijks, OntlastingspatroonOpties.anders].map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            {data.ontlastingspatroon === 'Anders' && (
                <FormGroup
                    hasErrors={errors.has('ontlasting_frequentie_per_week')}
                    isRequired={true}
                    label="Ontlasting frequentie"
                >
                    <Row columnGap={3}>
                        <Col span={1}>
                            <NumberInputContext
                                fieldKey="ontlasting_frequentie_per_week"
                                label="Ontlasting frequentie"
                                maximumFractionDigits={1}
                            />
                        </Col>
                        <Col>
                            <Text
                                fontWeight="medium"
                                mt={2}
                                size="sm"
                            >
                                per week
                            </Text>
                        </Col>
                    </Row>
                </FormGroup>
            )}
            <SelectFormGroup
                fieldKey="consistentie_ontlasting"
                isRequired={true}
                label="Consistentie ontlasting"
                onChange={handleChangeConsistentieOntlasting}
                options={sortedConsistentieOntlastingOpties.map((ontlastingOptie) => ({
                    label: ontlastingOptie,
                    value: ontlastingOptie,
                }))}
                placeholder="Selecteer een consistentie..."
            />
            {data.consistentie_ontlasting === ConsistentieOntlastingOpties.overig && (
                <TextInputFormGroup
                    fieldKey="consistentie_ontlasting_overig"
                    isRequired={true}
                    label="Toelichting overige consistentie"
                />
            )}
            <TextAreaFormGroup
                fieldKey="toelichting_speciele_anamnese"
                label="Toelichting speciële anamnese"
            />
        </FormSection>
    );

    async function handleChangeOntlastingspatroon(value: string | null): Promise<void> {
        return onChange(
            {
                ontlasting_frequentie_per_week: null,
                ontlastingspatroon: value as OntlastingspatroonOpties | null,
            },
            'ontlastingspatroon'
        );
    }

    async function handleChangeConsistentieOntlasting(value: string | null): Promise<void> {
        return onChange(
            {
                consistentie_ontlasting: value as ConsistentieOntlastingOpties | null,
                consistentie_ontlasting_overig: null,
            },
            'consistentie_ontlasting'
        );
    }
}
